<template>
  <div class="header-wrapper">
    <div class="header-bread-crumb">
      <span class="page-name" v-if="pageName" v-text="t(pageName.toString())" />
    </div>
    <div class="header-language-logout" v-click-outside="close">
      <div class="dropdown-trigger" @click="display = !display">
        <span class="dropdown-text">
          {{ t('admin_global_hi') }}, {{ user.name }}
          <i class="fa-solid fa-caret-down" />
        </span>
        <span class="user-icon">
          <i class="fa-solid fa-user" />
        </span>
      </div>
      <div class="dropdown-content" v-if="display && isDefault" @click.stop>
        <div class="dropdown-item" @click="isDefault = false">
          <span class="item-icon">
            <i class="fa-solid fa-language" />
          </span>
          {{ t('admin_global_language') }}
        </div>
        <div class="dropdown-item" @click="logOut()">
          <span class="item-icon">
            <i class="fa-solid fa-right-from-bracket" />
          </span>
          {{ t('admin_global_logout') }}
        </div>
      </div>
      <div class="dropdown-content" v-if="display && !isDefault" @click.stop>
        <div
          class="dropdown-item"
          v-for="language in languages"
          :key="language.id"
          @click="setLocale(language.code)"
        >
          <span class="language-flag">
            <img class="flag-image" :src="language.imageUrl" alt="Flag" />
          </span>

          {{ t(language.resource_name) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import languagesList from '@/data/languages.json'
  import { useAuthStore } from '@/pinia'
  import { GetLanguageData, UseI18n } from '@/utils-ts'
  import { computed, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  const authStore = useAuthStore()
  const { t } = UseI18n()
  const route = useRoute()
  const router = useRouter()
  const emit = defineEmits(['language'])

  const isDefault = ref<boolean>(true)
  const display = ref<boolean>(false)

  const user = computed(() => authStore.user)
  const pageName = computed(() => route.meta.pageResource)
  const languages = computed(() =>
    languagesList.map(item => {
      return {
        ...item,
        imageUrl: new URL(`../../assets/img/flags/${item.code}.svg`, import.meta.url).href,
      }
    }),
  )

  watch(display, () => {
    isDefault.value = true
  })

  const close = () => {
    display.value = false
    isDefault.value = true
  }

  const setLocale = async (code: string) => {
    emit('language', true)
    await GetLanguageData(code)
    emit('language', false)
    close()
  }

  const logOut = () => {
    authStore.Logout()
    router.push({ name: 'login' })
  }
</script>

<style lang="scss" scoped>
  .header-wrapper {
    width: 100%;
    height: 100%;
    background-color: $brandColor;
    @include flexible($justify: space-between, $align: center);
    color: $white;
    padding: 1rem 1.5rem;
    .header-bread-crumb {
      @include flexible($align: center);

      .sidebar-toggle {
        width: 2rem;
        height: 2rem;
        border-radius: 5px;
        cursor: pointer;
        border: 1px solid $white;
        margin-right: 0.5rem;
        color: $white;
        @include flexible($justify: center, $align: center);
      }
      .page-name {
        height: 2rem;
        padding: 0.5rem;
        border: 1px solid $white;
        border-radius: 5px;
        @include flexible($justify: center, $align: center);
      }
    }
    .header-language-logout {
      border-radius: 5px;
      cursor: pointer;
      position: relative;
      background-color: $white;
      @include media(xs, sm, md) {
        background-color: transparent;
      }
      .dropdown-trigger {
        color: $primary-text-color;
        display: flex;
        width: auto;
        padding: 0.5rem;
        align-items: center;
        .dropdown-text {
          margin-right: 0.5rem;
          @include media(xs, sm, md) {
            display: none;
          }
        }
        .user-icon {
          display: none;
          @include media(xs, sm, md) {
            display: flex;
            color: $white;
          }
        }
      }
      .dropdown-content {
        position: absolute;
        top: calc(100% + 1px);
        background-color: $white;
        display: flex;
        flex-direction: column;
        color: $primary-text-color;
        width: auto;
        min-width: 100%;
        border-radius: 5px;
        box-shadow: 0 0 5px 2px rgba(0, 0, 0, 0.2);
        padding: 0.5rem 0;
        z-index: 10;
        right: 0;
        @include media(xs, sm, md) {
          width: auto;
          min-width: 150px;
          right: 0;
        }
        .dropdown-item {
          padding: 0.25rem 0.5rem;
          font-size: 0.825rem;
          width: 100%;
          background-color: $white;
          align-items: center;
          display: flex;
          &:hover {
            background-color: #dedede;
          }
          .item-icon {
            margin-right: 0.5rem;
            height: 1.5rem;
            width: 1.5rem;
            display: flex;
            align-items: center;
            justify-content: center;
            svg {
              height: 16px;
            }
          }
          .language-flag {
            width: 2rem;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 0.5rem;
            .flag-image {
              width: 1.5rem;
            }
          }
        }
      }
    }
  }
</style>
